import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container, Typography, Box, Paper, Button, TextField, IconButton,
  List, ListItem, ListItemText, ListItemIcon, Collapse, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, CssBaseline, Alert, Snackbar,
  Checkbox, ListItemButton, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PlaceIcon from '@mui/icons-material/Place';
import SensorsIcon from '@mui/icons-material/Sensors';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LinkOffIcon from '@mui/icons-material/LinkOff';

// ─── helper ────────────────────────────────────────────────
const api = async (url, opts = {}) => {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  return res.json();
};

// ─── Inline‑edit dialog ───────────────────────────────────
function NameDialog({ open, title, initialValue, onClose, onSave }) {
  const [value, setValue] = useState(initialValue || '');
  useEffect(() => setValue(initialValue || ''), [initialValue, open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth margin="dense" label="Name"
          value={value} onChange={e => setValue(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!value.trim()} onClick={() => onSave(value.trim())}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Confirm‑delete dialog ────────────────────────────────
function ConfirmDialog({ open, message, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent><Typography>{message}</Typography></DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" onClick={onConfirm}>Delete</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Assign‑devices dialog ───────────────────────────────
function AssignDevicesDialog({ open, siteName, unassigned, onClose, onAssign }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  useEffect(() => { if (open) { setSearch(''); setSelected([]); } }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return unassigned;
    const q = search.toLowerCase();
    return unassigned.filter(d =>
      (d.name || '').toLowerCase().includes(q) || (d.deviceId || '').toLowerCase().includes(q)
    );
  }, [unassigned, search]);

  const toggleDevice = (deviceId) => {
    setSelected(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Devices to "{siteName}"</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth size="small" placeholder="Search devices…" sx={{ mb: 1, mt: 1 }}
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
          {filtered.length === 0 && (
            <ListItem><ListItemText secondary="No unassigned devices found" /></ListItem>
          )}
          {filtered.map(d => (
            <ListItemButton key={d.deviceId} onClick={() => toggleDevice(d.deviceId)} dense>
              <Checkbox edge="start" checked={selected.includes(d.deviceId)} tabIndex={-1} disableRipple size="small" />
              <ListItemIcon sx={{ minWidth: 32 }}><SensorsIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={d.name || d.deviceId} secondary={d.name ? d.deviceId : undefined} />
            </ListItemButton>
          ))}
        </List>
        {selected.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {selected.length} device(s) selected
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={selected.length === 0} onClick={() => onAssign(selected)}>Assign</Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
export default function HierarchyAdmin() {
  const [tree, setTree] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [expanded, setExpanded] = useState({});   // key = id
  const [dialog, setDialog] = useState(null);     // {type, level, parentId, id, name}
  const [confirmDel, setConfirmDel] = useState(null); // {level, id, name}
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [assignDialog, setAssignDialog] = useState(null); // {siteId, siteName}

  // ── load full tree ──
  const reload = useCallback(async () => {
    const data = await api('/api/admin/hierarchy/tree');
    setTree(data.tree || []);
    setUnassigned(data.unassigned || []);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggle = id => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const notify = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  // ── CREATE ──
  const handleCreate = async (level, parentId, name) => {
    let res;
    if (level === 'company') res = await api('/api/admin/companies', { method: 'POST', body: JSON.stringify({ name }) });
    if (level === 'area')    res = await api('/api/admin/areas',     { method: 'POST', body: JSON.stringify({ companyId: parentId, name }) });
    if (level === 'site')    res = await api('/api/admin/sites',     { method: 'POST', body: JSON.stringify({ areaId: parentId, name }) });
    if (res?.success) { notify(`Created ${level}: ${name}`); reload(); } else notify(res?.error || 'Failed', 'error');
  };

  // ── UPDATE ──
  const handleUpdate = async (level, id, name) => {
    const url = `/api/admin/${level === 'company' ? 'companies' : level === 'area' ? 'areas' : 'sites'}/${id}`;
    const res = await api(url, { method: 'PUT', body: JSON.stringify({ name }) });
    if (res?.success) { notify(`Renamed ${level}`); reload(); } else notify(res?.error || 'Failed', 'error');
  };

  // ── DELETE ──
  const handleDelete = async (level, id) => {
    const url = `/api/admin/${level === 'company' ? 'companies' : level === 'area' ? 'areas' : 'sites'}/${id}`;
    const res = await api(url, { method: 'DELETE' });
    if (res?.success) { notify(`Deleted ${level}`); reload(); } else notify(res?.error || 'Failed', 'error');
  };

  // ── UNASSIGN device ──
  const handleUnassign = async (deviceId) => {
    const res = await api('/api/admin/unassign_device', { method: 'POST', body: JSON.stringify({ deviceId }) });
    if (res?.success) { notify('Device unassigned'); reload(); } else notify(res?.error || 'Failed', 'error');
  };

  // ── ASSIGN devices to site ──
  const handleAssignDevices = async (deviceIds) => {
    let ok = 0, fail = 0;
    for (const deviceId of deviceIds) {
      const res = await api('/api/admin/assign_device_to_site', {
        method: 'POST', body: JSON.stringify({ deviceId, siteId: assignDialog.siteId })
      });
      if (res?.success) ok++; else fail++;
    }
    setAssignDialog(null);
    if (ok > 0) notify(`Assigned ${ok} device(s)${fail > 0 ? `, ${fail} failed` : ''}`);
    else notify('Failed to assign devices', 'error');
    reload();
  };

  // ── dialog helpers ──
  const openCreateDialog = (level, parentId) => setDialog({ type: 'create', level, parentId, id: null, name: '' });
  const openEditDialog = (level, id, name) => setDialog({ type: 'edit', level, parentId: null, id, name });
  const dialogSave = (name) => {
    if (dialog.type === 'create') handleCreate(dialog.level, dialog.parentId, name);
    else handleUpdate(dialog.level, dialog.id, name);
    setDialog(null);
  };

  const confirmDelete = (level, id, name) => setConfirmDel({ level, id, name });
  const doDelete = () => { handleDelete(confirmDel.level, confirmDel.id); setConfirmDel(null); };

  // ═══════════════════════  RENDER  ═══════════════════════
  return (
    <>
      <CssBaseline />
      <Container maxWidth="md" sx={{ mt: 2, mb: 10, height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Hierarchy Management</Typography>
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => openCreateDialog('company', null)}>
            Add Company
          </Button>
        </Box>

        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Company → Area (Building) → Site → Device  四级分类管理
        </Typography>

        {tree.length === 0 && <Alert severity="info">No hierarchy data yet. Start by adding a Company.</Alert>}

        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {/* ── Company level ── */}
        {tree.map(company => (
          <Paper key={company._id} elevation={2} sx={{ mb: 1 }}>
            <ListItem
              secondaryAction={
                <Box>
                  <IconButton size="small" onClick={() => openCreateDialog('area', company._id)} title="Add Area"><AddIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => openEditDialog('company', company._id, company.name)} title="Rename"><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => confirmDelete('company', company._id, company.name)} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              }
            >
              <ListItemIcon onClick={() => toggle(company._id)} sx={{ cursor: 'pointer' }}>
                {expanded[company._id] ? <ExpandLess /> : <ExpandMore />}
              </ListItemIcon>
              <ListItemIcon><BusinessIcon color="primary" /></ListItemIcon>
              <ListItemText
                primary={company.name}
                secondary={`${company.areas?.length || 0} area(s)`}
                onClick={() => toggle(company._id)}
                sx={{ cursor: 'pointer' }}
              />
            </ListItem>

            {/* ── Area level ── */}
            <Collapse in={expanded[company._id]}>
              <List disablePadding sx={{ pl: 4 }}>
                {company.areas?.map(area => (
                  <React.Fragment key={area._id}>
                    <ListItem
                      secondaryAction={
                        <Box>
                          <IconButton size="small" onClick={() => openCreateDialog('site', area._id)} title="Add Site"><AddIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => openEditDialog('area', area._id, area.name)} title="Rename"><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => confirmDelete('area', area._id, area.name)} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                        </Box>
                      }
                    >
                      <ListItemIcon onClick={() => toggle(area._id)} sx={{ cursor: 'pointer' }}>
                        {expanded[area._id] ? <ExpandLess /> : <ExpandMore />}
                      </ListItemIcon>
                      <ListItemIcon><LocationCityIcon color="secondary" /></ListItemIcon>
                      <ListItemText
                        primary={area.name}
                        secondary={`${area.sites?.length || 0} site(s)`}
                        onClick={() => toggle(area._id)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </ListItem>

                    {/* ── Site level ── */}
                    <Collapse in={expanded[area._id]}>
                      <List disablePadding sx={{ pl: 4 }}>
                        {area.sites?.map(site => (
                          <React.Fragment key={site._id}>
                            <ListItem
                              secondaryAction={
                                <Box>
                                  <IconButton size="small" onClick={() => setAssignDialog({ siteId: site._id, siteName: site.name })} title="Assign Devices"><AddIcon fontSize="small" /></IconButton>
                                  <IconButton size="small" onClick={() => openEditDialog('site', site._id, site.name)} title="Rename"><EditIcon fontSize="small" /></IconButton>
                                  <IconButton size="small" onClick={() => confirmDelete('site', site._id, site.name)} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                                </Box>
                              }
                            >
                              <ListItemIcon onClick={() => toggle(site._id)} sx={{ cursor: 'pointer' }}>
                                {expanded[site._id] ? <ExpandLess /> : <ExpandMore />}
                              </ListItemIcon>
                              <ListItemIcon><PlaceIcon color="success" /></ListItemIcon>
                              <ListItemText
                                primary={site.name}
                                secondary={`${site.devices?.length || 0} device(s)`}
                                onClick={() => toggle(site._id)}
                                sx={{ cursor: 'pointer' }}
                              />
                            </ListItem>

                            {/* ── Device level ── */}
                            <Collapse in={expanded[site._id]}>
                              <List disablePadding sx={{ pl: 4 }}>
                                {site.devices?.map(device => (
                                  <ListItem key={device.deviceId}
                                    secondaryAction={
                                      <IconButton size="small" onClick={() => handleUnassign(device.deviceId)} title="Unassign">
                                        <LinkOffIcon fontSize="small" />
                                      </IconButton>
                                    }
                                  >
                                    <ListItemIcon><SensorsIcon /></ListItemIcon>
                                    <ListItemText
                                      primary={device.name || device.deviceId}
                                      secondary={device.deviceId}
                                    />
                                  </ListItem>
                                ))}
                                {(!site.devices || site.devices.length === 0) && (
                                  <ListItem><ListItemText secondary="No devices assigned" /></ListItem>
                                )}
                              </List>
                            </Collapse>
                          </React.Fragment>
                        ))}
                        {(!area.sites || area.sites.length === 0) && (
                          <ListItem sx={{ pl: 4 }}><ListItemText secondary="No sites yet" /></ListItem>
                        )}
                      </List>
                    </Collapse>
                  </React.Fragment>
                ))}
                {(!company.areas || company.areas.length === 0) && (
                  <ListItem><ListItemText secondary="No areas yet" /></ListItem>
                )}
              </List>
            </Collapse>
          </Paper>
        ))}

        {/* ── Unassigned Devices ── */}
        {unassigned.length > 0 && (
          <Paper elevation={1} sx={{ mt: 2, p: 2, flexShrink: 0 }}>
            <Typography variant="subtitle1" gutterBottom>Unassigned Devices ({unassigned.length})</Typography>
            <Box display="flex" flexWrap="wrap" gap={1} sx={{ maxHeight: 200, overflow: 'auto' }}>
              {unassigned.map(d => (
                <Chip key={d.deviceId} icon={<SensorsIcon />} label={d.name || d.deviceId} variant="outlined" size="small" />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Click the + button on a Site to assign devices, or go to Edit → select a device → use Hierarchy selector
            </Typography>
          </Paper>
        )}
        </Box>
      </Container>

      {/* ── Dialogs ── */}
      <NameDialog
        open={!!dialog}
        title={dialog ? `${dialog.type === 'create' ? 'Create' : 'Rename'} ${dialog.level}` : ''}
        initialValue={dialog?.name || ''}
        onClose={() => setDialog(null)}
        onSave={dialogSave}
      />

      <ConfirmDialog
        open={!!confirmDel}
        message={confirmDel ? `Delete "${confirmDel.name}"? All child data will be removed and devices will be unassigned.` : ''}
        onClose={() => setConfirmDel(null)}
        onConfirm={doDelete}
      />

      <AssignDevicesDialog
        open={!!assignDialog}
        siteName={assignDialog?.siteName || ''}
        unassigned={unassigned}
        onClose={() => setAssignDialog(null)}
        onAssign={handleAssignDevices}
      />

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </>
  );
}
